import { InformationType } from "@/common/models";
import { delayBy, index_by_path } from "@/fp";
import { cord, formatInput, gql2 } from "@/gql/lib";
import { CountItem } from "./model";
import { ApiResult, apiCall } from "@/common/api/ApiResult.model";
import { ICONS } from "@/common/icons.map";
import { Colors } from "@/theme";
import {
  ActionEnum,
  AxisType,
  QueryTier1countArgs,
  Tier1Response,
  TimeAxisRangeInput,
  TimeAxisType,
} from "@/gql/types";
import { SharedState } from "@/common/state";
import { GqlCallsKey } from "@/mock/demo/gql.resolver";
import { get_risky_users_count_for_l0 } from "@/views/customize/in-motion/security-policies/risky-users/data/api";
import { querifyArray } from "@/utils/url";
import axios from "axios";
import moment from "moment";

const count_by_label_id = index_by_path<Tier1Response, "y">(
  "y.value",
  // @ts-ignore
  ""
);

export const get_events_count_for_L0 = gql2<InformationType[], CountItem[]>(
  "ms-365-activities.events-count-for-l0",
  (l0s, { frame }) => {
    const q = (frame: TimeAxisRangeInput): QueryTier1countArgs => {
      return {
        params: {
          coordinate: {
            x: cord(AxisType.None),
            y: {
              type: AxisType.ContentLabel,
              values: l0s.map((d) => d.id),
            },
            z: frame,
          },
          action: ActionEnum.List,
        },
      };
    };
    return {
      query: `{
         tier1count(params:${formatInput(q(frame).params)}){
          y{value}
          count
        }
    }`,
      mapper: (res) => {
        // @ts-ignore
        const indexed_result = count_by_label_id(res.tier1count);
        return l0s
          .map((label) => {
            return {
              id: label.id,
              name: label.labelName,
              count: indexed_result[label.id]
                ? indexed_result[label.id].count!
                : 0,
              icon: ICONS.L0_ICONS.get(label.code),
            };
          })
          .sort((a, b) => b.count - a.count);
      },
    };
  }
);

export const get_risks_count_for_l0 = gql2<
  {
    l0s: InformationType[];
    severity: number;
  },
  CountItem[]
>("ms-365-activities.risk-events-for-l0", ({ l0s, severity }, { frame }) => {
  const q = (frame: TimeAxisRangeInput): QueryTier1countArgs => {
    return {
      params: {
        coordinate: {
          x: {
            type: AxisType.Severity,
            values: [severity.toString()],
          },
          y: {
            type: AxisType.ContentLabel,
            values: l0s.map((d) => d.id),
          },
          z: frame,
        },
        action: ActionEnum.List,
      },
    };
  };
  return {
    query: `{
         tier1count(params:${formatInput(q(frame).params)}){
          y{value}
          count
        }
    }`,
    mapper: (res) => {
      const indexed_result = count_by_label_id(res.tier1count);
      return l0s
        .map((label) => {
          return {
            id: label.id,
            name: label.labelName,
            count: indexed_result[label.id]
              ? indexed_result[label.id].count!
              : 0,
            icon: ICONS.L0_ICONS.get(label.code),
          };
        })
        .sort((a, b) => b.count - a.count);
    },
  };
});

const get_share_desc = (entity: AxisType, label_id: string) => {
  const label = SharedState.allInfoTypes.value[label_id].labelName;
  switch (entity) {
    case AxisType.EmployeeReceiverType:
      return `# of employees that received ${label} information`;
    case AxisType.EmployeeSenderType:
      return `# of employees that shared ${label} information`;
    case AxisType.PersonalAddressType:
      return `# of personal addresses that received ${label} information`;
    case AxisType.OrganizationReceiverType:
      return `# of organizations that received ${label} information`;
    case AxisType.ExternalReceiverType:
    default:
      return `# of external contacts that received ${label} information`;
  }
};

export const get_count_for_entity_type = (type: AxisType, tag: GqlCallsKey) =>
  gql2<
    string[],
    {
      type: string;
      items: Array<{ label: string; count: number; desc: string }>;
    }
  >(tag, (labels, { frame }) => {
    const q = (
      entity_type: AxisType,
      frame: TimeAxisRangeInput
    ): QueryTier1countArgs => {
      return {
        params: {
          coordinate: {
            x: cord(entity_type),
            y: {
              type: AxisType.ContentLabel,
              values: labels,
            },
            z: frame,
          },
          action: ActionEnum.List,
        },
      };
    };
    return {
      query: `{
      tier1count(params:${formatInput(q(type, frame).params)}){
        y{value}
        count
        uniqs
      }

  }`,
      mapper: (res) => {
        const indexed_res = count_by_label_id(res.tier1count);
        return {
          type,
          items: labels.map((label_id) => {
            return {
              label: label_id,
              count: indexed_res[label_id] ? indexed_res[label_id].uniqs! : 0,
              desc: get_share_desc(type, label_id),
            };
          }),
        };
      },
    };
  });

const employee_receiver_call = get_count_for_entity_type(
  AxisType.EmployeeReceiverType,
  "ms-365-activities.information-shared-employee-receiver"
);
const employee_sender_call = get_count_for_entity_type(
  AxisType.EmployeeSenderType,
  "ms-365-activities.information-shared-employee-sender"
);
const org_receiver_call = get_count_for_entity_type(
  AxisType.OrganizationReceiverType,
  "ms-365-activities.information-shared-org-receiver"
);
const external_contact_receiver_call = get_count_for_entity_type(
  AxisType.ExternalReceiverType,
  "ms-365-activities.information-shared-personal-receiver"
);
export const get_information_shared_per_entity_per_label = async (
  labels: string[]
): Promise<ApiResult<
  Array<{
    type: string;
    items: Array<{ label: string; count: number; desc: string }>;
  }>
>> => {
  const res = await Promise.all([
    employee_sender_call(labels),
    employee_receiver_call(labels),
    org_receiver_call(labels),
    external_contact_receiver_call(labels),
  ]);
  if (res.every((s) => s.success)) {
    return {
      success: true,
      // @ts-ignore
      value: res.map((d) => d.value),
    };
  } else {
    return {
      success: false,
      code: 400,
    };
  }
};

export const get_counts_per_application = gql2<
  {
    l0s: InformationType[];
    apps: string[];
  },
  CountItem[]
>("ms-365-activities.data-in-apps", ({ l0s, apps }, { frame }) => {
  const q: QueryTier1countArgs = {
    params: {
      coordinate: {
        x: {
          type: AxisType.Application,
          values: apps,
        },
        y: {
          type: AxisType.ContentLabel,
          values: l0s.map((d) => d.id),
        },
        z: frame,
      },
      action: ActionEnum.List,
    },
  };
  return {
    query: `{
         tier1count(params:${formatInput(q.params)}){
          x{value}
          y{value}
          count
        }
    }`,
    mapper: (res) => {
      const base_result = apps.reduce((acc, id) => {
        acc[id] = {
          count: 0,
          id,
          name: id,
          icon: ICONS.SERVICE.byString(id),
          // @ts-ignore
          color: Colors[id]!,
        };
        return acc;
      }, {} as Record<string, CountItem>);
      res.tier1count.forEach((val: any) => {
        base_result[val.x.value].count += val.count;
      });
      return Object.values(base_result).sort((a, b) => b.count - a.count);
    },
  };
});

export const get_relative_mip_counts_per_l0 = async (
  l0s: string[]
): Promise<ApiResult<
  Array<{
    label_id: string;
    label_icon: string;
    counts: {
      total: number;
      ms: number;
      cognni: number;
    };
  }>
>> => {
  await delayBy(2000);
  return {
    success: true,
    value: l0s.map((label_id) => {
      const label = SharedState.allInfoTypes.value[label_id];
      const total = 100;
      const cognni = Math.floor(Math.random() * 60);
      const ms = Math.floor(Math.random() * 40);
      return {
        label_id,
        label_icon: ICONS.L0_ICONS.get(label.code),
        counts: {
          total: total,
          cognni,
          ms,
        },
        // source_id
      };
    }),
  };
};

export const get_risky_users_results_for_all_l0s = async (
  l0s: InformationType[],
  time_frame: string,
  frame: "Month" | "Week"
): Promise<ApiResult<CountItem[]>> => {
  const res = await Promise.all(
    l0s.map((d) =>
      get_risky_users_count_for_l0({
        l0_id: d.id,
        date: time_frame,
        frame,
      })
    )
  );
  if (res.every((s) => s.success)) {
    return {
      success: true,
      // @ts-ignore
      value: res.map((d) => {
        return {
          // @ts-ignore
          ...d.value,
          // @ts-ignore
          name: SharedState.allInfoTypes.value[d.value.id].labelName,
        };
      }),
    };
  } else {
    return {
      success: false,
      code: 400,
    };
  }
};

export const get_mass_download_for_all_l0s = apiCall<
  { l0s: InformationType[]; time_frame: string; time_type: TimeAxisType },
  CountItem[]
>("mass-download.counters-summary", async ({ l0s, time_frame, time_type }) => {
  const time_param = {} as {
    inverseCalendarWeekNumber?: number;
    inverseCalendarMonthNumber?: number;
  };
  if (time_type == TimeAxisType.Month) {
    const time_diff = Math.abs(moment().diff(moment(time_frame), "months"));
    time_param.inverseCalendarMonthNumber = time_diff;
  } else if (time_type == TimeAxisType.Week) {
    const time_diff = Math.abs(moment().diff(moment(time_frame), "days"));
    console.log("time_frame:::", time_frame, time_diff);
    time_param.inverseCalendarWeekNumber = time_diff < 7 ? 0 : 1;
  }
  const l0_ids = l0s.map((d) => d.id);
  const q = {
    ...time_param,
    infoTypeCategoryIds: querifyArray({
      infoTypeCategoryIds: [l0_ids[0], ...l0_ids],
    }),
  };
  const query_params = Object.entries(q)
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
  const res = await axios.get(
    `extproxy/integrations/anomalous-downloader-counters?` + query_params
  );

  return {
    success: true,
    value: res.data.value.anomalousDownloaderCounters.map((s: any) => {
      return {
        id: s.infoTypeCategoryId,
        count: s.itemCount,
        name: SharedState.allInfoTypes.value[s.infoTypeCategoryId].labelName,
      };
    }),
  };
});

export const get_high_severity_events_for_all_l0s = gql2<
  InformationType[],
  CountItem[]
>("ms-365-activities.high-risk-events-findings", (l0s, { frame }) => {
  const q = (frame: TimeAxisRangeInput): QueryTier1countArgs => {
    return {
      params: {
        coordinate: {
          x: {
            type: AxisType.Severity,
            values: ["3"],
          },
          y: {
            type: AxisType.ContentLabel,
            values: l0s.map((d) => d.id),
          },
          z: frame,
        },
        action: ActionEnum.UniqCount,
      },
    };
  };
  return {
    query: `{
        tier1count(params:${formatInput(q(frame).params)}){
         y{value}
         count
       }
   }`,
    mapper: (res) => {
      const indexed_res = count_by_label_id(res.tier1count);
      return l0s.map(({ labelName, id }) => {
        return {
          id: id,
          count: indexed_res[id] ? indexed_res[id].count! : 0,
          name: labelName,
        };
      });
    },
  };
});
